import { create, type StateCreator, type StoreApi } from 'zustand';
import { fallbackLng } from '@unifyapps/i18n/settings';
import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import type { PageVariableState } from '@unifyapps/defs/types/pageVariable';
import type { RefObject } from 'react';
import { DEFAULT_DEVICE_VARIANT } from '@unifyapps/defs/types/deviceVariant';
import type { DataSourceEntity } from '@unifyapps/defs/types/dataSource';
import type { PageFunctionState } from '@unifyapps/defs/types/pageFunction';
import DataSourceHelper from '../../helper/DataSourceHelper';
import createObjectSetters from '../../../utils/setters/createObjectSetters';
import type { DataSourceRecordStoreState } from '../DataSourceRecordStore';
import type { RemountFunction } from '../../../components/Remount/Remount';
import getDeviceVariantActions from './actions/deviceVariantAction';
import getInterfaceSessionStorageAction from './actions/getInterfaceSessionStorageAction';
import type {
  BlockLifecycleStatus,
  DataSourceMetaType,
  GlobalStateStore,
  GlobalStateStoreInitialValue,
  GlobalStateStoreType,
  ManuallyTriggeredDataSourceType,
  QueryRequestResult,
} from './types';
import DependencyFlowHelper from './helpers/DependencyFlowHelper';
import { getInitialDatasourceDependencyFlow } from './utils/initial';
import getDataSourceActions from './actions/dataSourceActions';

export const createGlobalStateStore = ({
  dataSourceRecords,
  initial,
  getDataSourceRecordsStore,
  remount,
  interfaceSessionStorage,
  setInterfaceSessionStorage,
  interfaceId,
}: {
  initial?: GlobalStateStoreInitialValue;
  dataSourceRecords?: Record<string, DataSourceEntity | undefined>;
  getDataSourceRecordsStore: StoreApi<DataSourceRecordStoreState>['getState'];
  remount: RemountFunction;
  interfaceId: string;
  interfaceSessionStorage: Record<string, unknown> | undefined;
  setInterfaceSessionStorage: (value: Record<string, unknown> | undefined) => Promise<void>;
}) => {
  const initializer: StateCreator<GlobalStateStoreType> = (set, get) => {
    const { setSingle: setDataSourceState, remove: removeDataSourceState } = createObjectSetters<
      GlobalStateStoreType,
      'dataSources',
      QueryRequestResult
    >('dataSources', set);

    const { setSingle: setManualDataSourceState, remove: removeManualDataSourceState } =
      createObjectSetters<GlobalStateStoreType, 'manualDataSources', QueryRequestResult>(
        'manualDataSources',
        set,
      );

    const {
      setSingle: setBlockState,
      updateSingle: updateBlockState,
      setMultiple: setBlocksState,
      removeMultiple: removeBlocksState,
    } = createObjectSetters<GlobalStateStoreType, 'blocks', BlockStateUnionType>('blocks', set);

    const { setSingle: setPreviewTriggeredDataSource, remove: removePreviewTriggeredDataSource } =
      createObjectSetters<GlobalStateStoreType, 'previewTriggeredDataSources', boolean>(
        'previewTriggeredDataSources',
        set,
      );

    const { setSingle: setRunTriggeredDataSource, remove: removeRunTriggeredDataSource } =
      createObjectSetters<GlobalStateStoreType, 'runTriggeredDataSources', boolean>(
        'runTriggeredDataSources',
        set,
      );

    const { setSingle: setPageVariableState, setMultiple: setPageVariablesState } =
      createObjectSetters<GlobalStateStoreType, 'pageVariables', PageVariableState>(
        'pageVariables',
        set,
      );

    const { setSingle: setPageFunctionState } = createObjectSetters<
      GlobalStateStoreType,
      'pageFunctions',
      PageFunctionState
    >('pageFunctions', set);

    const {
      setSingle: setManuallyTriggeredDataSource,
      removeMultiple: removeManuallyTriggeredDataSources,
    } = createObjectSetters<
      GlobalStateStoreType,
      'manuallyTriggeredDataSources',
      ManuallyTriggeredDataSourceType
    >('manuallyTriggeredDataSources', set);

    const { setMultiple: setLocation } = createObjectSetters<
      GlobalStateStoreType,
      'location',
      string
    >('location', set);

    const setNavigationDrawerState = (isOpen: boolean) =>
      set((state) => ({ ...state, isNavigationDrawerOpen: isOpen }));

    const { setMultiple: setUserContext } = createObjectSetters<
      GlobalStateStoreType,
      'userContext',
      string
    >('userContext', set);

    const { setSingle: setDataSourceMeta, remove: removeDataSourceMeta } = createObjectSetters<
      GlobalStateStoreType,
      'dataSourcesMeta',
      DataSourceMetaType
    >('dataSourcesMeta', set);

    const { setSingle: setBlockLifecycle } = createObjectSetters<
      GlobalStateStoreType,
      'blockLifecycles',
      BlockLifecycleStatus
    >('blockLifecycles', set);

    const setAggregationMetadataInDSMeta = (
      dataSourceId: string,
      data: QueryRequestResult | undefined,
    ) => {
      const currentMeta = get().dataSourcesMeta[dataSourceId];
      setDataSourceMeta(dataSourceId, { ...currentMeta, aggregationMetadata: data });
    };

    const setPreviewDataInDSMeta = (dataSourceId: string, data: QueryRequestResult | undefined) => {
      const currentMeta = get().dataSourcesMeta[dataSourceId];
      setDataSourceMeta(dataSourceId, { ...currentMeta, previewResult: data });
    };

    const { setSingle: setBlockRef, remove: removeBlockRef } = createObjectSetters<
      GlobalStateStoreType,
      'blockRefs',
      RefObject<unknown>
    >('blockRefs', set);

    const { setMultiple: setEnvVariables } = createObjectSetters<
      GlobalStateStoreType,
      'envVariables',
      string
    >('envVariables', set);

    const removeDataSourceFromGlobalState = (dataSourceId: string) => {
      removeDataSourceState(dataSourceId);
      removeDataSourceMeta(dataSourceId);
      removePreviewTriggeredDataSource(dataSourceId);
      removeRunTriggeredDataSource(dataSourceId);
      removeManuallyTriggeredDataSources([dataSourceId]);
    };

    const dataSourceActions = getDataSourceActions({
      get,
      set,
      setPreviewTriggeredDataSource,
      getDataSourceRecordsStore,
      setManuallyTriggeredDataSource,
      setRunTriggeredDataSource,
      removeDataSourceFromGlobalState,
    });

    const setPreBuiltFlow: GlobalStateStoreType['actions']['setPreBuiltFlow'] = ({
      data,
      type,
    }) => {
      set((state) => {
        return {
          ...state,
          flows: {
            ...state.flows,
            [type]: data,
          },
        };
      });
    };

    const removePreBuiltFlow: GlobalStateStoreType['actions']['removePreBuiltFlow'] = (type) => {
      set((state) => {
        return {
          ...state,
          flows: {
            ...state.flows,
            [type]: undefined,
          },
        };
      });
    };

    const resetBlockRefs = () => {
      set((state) => {
        return {
          ...state,
          blockRefs: {},
        };
      });
    };

    const dataSourceDependenciesGraph = dataSourceRecords
      ? DataSourceHelper.getDataSourceIntraDependencies(dataSourceRecords)
      : {};

    const initialDataSourceDependencyFlow = getInitialDatasourceDependencyFlow(dataSourceRecords);
    const completeDatasourceDependencies = DependencyFlowHelper.getDependencyFlowStatus({
      dependencyFlowInstance: {},
      entities: initialDataSourceDependencyFlow,
      intraDependencies: dataSourceDependenciesGraph,
    });

    const setIsInitialized = () => {
      set((state) => {
        return {
          ...state,
          isInitialized: true,
        };
      });
    };

    const reset = () => {
      set(() => storeObject);
    };

    const setContextualDialogForBlocks = (
      contextualDialogForBlocks: Record<string, string | undefined>,
    ) => {
      set((state) => {
        return {
          ...state,
          contextualDialogForBlocks,
        };
      });
    };

    const state: GlobalStateStore = {
      envVariables: {},
      location: {},
      blocks: {},
      blockLifecycles: {},
      pageInputs: {},
      dataSources: {},
      manualDataSources: {},
      contextualDialogForBlocks: {},
      dataSourcesMeta: {},
      previewTriggeredDataSources: {},
      runTriggeredDataSources: {},
      manuallyTriggeredDataSources: {},
      userContext: {
        locale: fallbackLng,
      },
      pageVariables: {},
      blockRefs: {},
      flows: {},
      deviceDetails: {
        device: DEFAULT_DEVICE_VARIANT,
      },
      isNavigationDrawerOpen: false,
      permissions: {},
      pageFunctions: {},
      interfaceSessionStorage: interfaceSessionStorage?.[interfaceId] ?? {},
      dependencyFlowManager: {
        dataSources: completeDatasourceDependencies,
      },
      intraDependencies: {
        dataSources: dataSourceDependenciesGraph,
      },
      ...initial,
    };

    const storeObject: GlobalStateStoreType = {
      ...state,
      actions: {
        removeManuallyTriggeredDataSources,
        setBlocksState,
        removeBlocksState,
        setDataSourceState,
        setBlockState,
        updateBlockState,
        setBlockLifecycle,
        setUserContext,
        setLocation,
        setNavigationDrawerState,
        setManualDataSourceState,
        removeManualDataSourceState,
        datasource: dataSourceActions,
        setBlockRef,
        setEnvVariables,
        resetBlockRefs,
        removeBlockRef,
        setPageVariableState,
        setPageVariablesState,
        setPageFunctionState,
        setContextualDialogForBlocks,
        setPreviewDataInDSMeta,
        setAggregationMetadataInDSMeta,
        setPreBuiltFlow,
        removePreBuiltFlow,
        deviceVariantActions: getDeviceVariantActions(set),
        setInterfaceSessionStorage: getInterfaceSessionStorageAction(
          get,
          set,
          interfaceSessionStorage,
          setInterfaceSessionStorage,
        ),
        setIsInitialized,
        reset,
        remount,
      },
    };

    return storeObject;
  };
  return create<GlobalStateStoreType>()(initializer);
};
